import { Box } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import Linkify from 'linkify-react';
import React, {
  CSSProperties,
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { EditorState } from 'remirror';
import Typography from '@ui/Typography';
import { useProductUsageQuery } from 'components/ProductUsage/operation.generated';
import { useIsBusiness, useIsManagedUser } from 'context/user/UserContext';
import {
  ClipActor,
  Project,
  StudioProjectVersion,
} from '../../../server/graphql/__generated.types';
import useClipCreator, {
  determinePendingReplacements,
} from '../../context/clips/useClipCreator';
import useBreakpoint from '../../lib/useBreakpoint';
import usePrevious from '../../lib/usePrevious';
import EditorV2, { EditorV2Props } from '../EditorV2';
import { getCoreEditorRef } from '../../context/editorV2/CoreEditorContext';
import EditorError from './EditorError';
import { getFormattedEditorLimits } from './utils';
import { usePromptsContext } from '../../context/prompts/PromptsContext';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { setHasCues } from '../../store/CuesSlice';
import { useEventsContext } from '../../context/events/EventsContext';
import { EventName } from '../../services/EventTrackingService';
import { hasCues as hasCuesUtil } from '../EditorV2/utils';
import VisuallyHidden from '../VisuallyHidden';

import {
  EditMode,
  repopulate,
  setRenderMode,
  setScript,
  setShouldBeSingleTakeOnly,
} from '../../store/EditorSlice';
import useIsFirstRender from '../../lib/useIsFirstRender';
import EditorPrimaryActionButton from './Controls/EditorPrimaryActionButton';
import CJKEditorPrimaryActionButton from './Controls/CJKEditorPrimaryActionButton';
import { useStudioClipPreviewListContext } from '../../context/previewClip/StudioPreviewClipContext';
import useEvaluateStudioProjectVersionFeatures from '../Studio/useEvaluateStudioProjectVersionFeatures';

const useStyles = makeStyles()(theme => ({
  textareaContainer: {
    overflowY: 'auto',
    ...(theme.mixins.scrollBar as CSSProperties),
  },
  textarea: {
    backgroundColor: theme.palette.common.black,
    border: 0,
    color: theme.palette.common.white,
    fontFamily: theme.typography.body1.fontFamily,
    fontSize: '1.5em',
    lineHeight: 2 / 1.5,
    outline: 0,
    padding: 0,
    resize: 'none',
    width: '100%',
    '&::placeholder': {
      color: '#333333',
      opacity: 1,
    },
  },
  errorLink: {
    color: theme.palette.error.light,
  },
  tooltip: {
    position: 'relative',
    left: 195,
  },
}));

export interface EditorOnChangeProps {
  hasCues: boolean;
}

export interface EditorProps {
  project: Project;
  clipActor: ClipActor;
  isLocked?: boolean;
  isEditable: boolean;
  canAccessCuesMode: boolean;
  isCuesMode: boolean;
  previewFn: ReturnType<typeof useClipCreator>['3'];
  isTTSRequestLoading: boolean;
  cancelPreviewFn: ReturnType<typeof useClipCreator>['2'];
  previewClipError?: string | null;
  downloadError?: string | null;
  setDownloadError?: (downloadError: string) => void;
}

const RenderModeType = {
  all: 'Create single take',
  paragraph: 'Render by paragraph',
  sentence: 'Render by sentence',
} as const;

const Editor = ({
  project,
  clipActor,
  isLocked,
  isEditable,
  canAccessCuesMode,
  isCuesMode,
  previewFn,
  cancelPreviewFn,
  isTTSRequestLoading,
  previewClipError,
  downloadError,
  setDownloadError,
}: EditorProps) => {
  const projectId = project.id;
  const { classes } = useStyles();
  const coreEditor = getCoreEditorRef();
  const { hasCues } = useAppSelector(state => state.cues);
  const [, dispatch] = usePromptsContext();
  const appDispatch = useAppDispatch();
  const [eventTracking] = useEventsContext();
  const { data: usageData } = useProductUsageQuery();
  const editorState = useAppSelector(state => state.editor);
  const isBusinessUser = useIsBusiness();
  const isManagedUser = useIsManagedUser();
  const { hasCueChanged } = useStudioClipPreviewListContext();
  const projectVersionFeatures =
    useEvaluateStudioProjectVersionFeatures(project);
  const [upgradePlanError, setUpgradePlanError] = useState(false);
  const extensionOptionsOverrides: EditorV2Props['extensionOptionsOverrides'] =
    useMemo(
      () => ({
        cueOptions: {
          useInsertPauseFloatingButton: true,
        },
        paragraphOptions: {
          useParagraphToolbar: true,
        },
        phoneticReplacementOptions: {},
        tokenizerOptions: {},
        tokenSelectionOptions: {},
        placeholderOptions: {},
      }),
      []
    );

  /**
   * TTS Rendering action/state
   */
  const [create, { loading, error: submitError }, cancel] = useClipCreator(
    projectId,
    clipActor,
    setUpgradePlanError
  );

  const handleOnRenderModeChanged = useCallback(
    (value: string) => {
      let renderModeForStateUpdate: keyof typeof RenderModeType | undefined;
      switch (value) {
        case RenderModeType.all:
          renderModeForStateUpdate = 'all';
          break;
        case RenderModeType.paragraph:
          renderModeForStateUpdate = 'paragraph';
          break;
        case RenderModeType.sentence:
          renderModeForStateUpdate = 'sentence';
          break;
        default:
          break;
      }

      if (renderModeForStateUpdate) {
        // Clear active submit error when render mode changes so the new editor error can be displayed
        setActiveSubmitError(null);
        appDispatch(setRenderMode(renderModeForStateUpdate));
      }
    },
    [appDispatch]
  );

  /**
   * A `submitError` only goes away when the user hits the 'Create' button one more time. For
   * a better user experience we hide the `submitError` after a users makes a change to the
   * text so it doesn't linger longer than necessary.
   */
  const errorToUse =
    submitError ||
    previewClipError ||
    downloadError ||
    editorState.error?.message ||
    null;
  const [activeSubmitError, setActiveSubmitError] = useState(errorToUse);
  const previousSubmitError = usePrevious(errorToUse);
  useEffect(() => {
    if (
      !activeSubmitError &&
      errorToUse &&
      // only set `activeSubmitError` if the value for submitError changed
      errorToUse !== previousSubmitError
    ) {
      setActiveSubmitError(errorToUse);
    }
  }, [activeSubmitError, previousSubmitError, errorToUse]);

  useEffect(() => {
    if (downloadError) {
      setActiveSubmitError(downloadError);
      setUpgradePlanError(true);
    }
    if (
      downloadError &&
      usageData &&
      setDownloadError &&
      !(isBusinessUser || isManagedUser)
    ) {
      dispatch({
        type: 'OpenUpgradePlanPrompt',
        payload: {
          isTrialAccount: false,
          isDownloadLimit: true,
          limitAmount:
            usageData.user.productAccessLimits.takeCountDownloadLimit ?? 0,
          onClick: () => {
            setUpgradePlanError(false);
            setActiveSubmitError(null);
            dispatch({
              type: 'OpenSubscriptionTiersPrompt',
              payload: {
                upgradeContext: 'access_limit_clips',
                upgradePath: 'Dashboard > Studio > Clips Limit Modal',
              },
            });
          },
        },
      });
      setDownloadError('');
    }
  }, [
    activeSubmitError,
    downloadError,
    usageData,
    dispatch,
    setDownloadError,
    isBusinessUser,
    isManagedUser,
    errorToUse,
  ]);

  /**
   * Reset active submit error if user switches from Script <> Cues
   */
  useEffect(() => {
    if (!upgradePlanError) setActiveSubmitError(null);
  }, [isCuesMode, upgradePlanError]);

  /**
   * Finally we derive the error message show to the user. if and `activeSubmitError`
   * is present, we pick that. Otherwise, we show the relevant editorError.
   */
  const editorErrorMessage = editorState.error?.message;
  const previousEditorValue = usePrevious(editorState.script.text);
  useEffect(() => {
    if (
      activeSubmitError &&
      previousEditorValue !== editorState.script.text &&
      !upgradePlanError
    )
      setActiveSubmitError(null);
  }, [
    activeSubmitError,
    editorState.script.text,
    previousEditorValue,
    upgradePlanError,
  ]);

  /**
   * Control state of the editor error message with an additional state
   * This is so that we can re-render the message and trigger the aria role alert
   * to announce error message.
   */
  const prevEditorError = usePrevious(editorErrorMessage);
  const [editorErr, setEditorErr] = useState<string | null>(null);
  /**
   * Upon a render mode change, the editor state doesn't clear out error messages,
   * it will validate and immediately update to the next error message.
   * This change is so that we can clear the inline error and re-render it.
   */
  useEffect(() => {
    setEditorErr(null);
    /**
     * Set the state again on the next cycle by using setTimeout hack
     */
    setTimeout(() => {
      if (editorErrorMessage) setEditorErr(editorErrorMessage);
    }, 0);
  }, [editorErrorMessage, prevEditorError]);

  const inlineError = useMemo<string | null | ReactElement>(() => {
    if (activeSubmitError)
      return (
        <Linkify
          options={{
            target: '_blank',
            rel: 'noopener noreferrer',
            className: classes.errorLink,
          }}
        >
          {activeSubmitError}
        </Linkify>
      );

    return editorErr;
  }, [activeSubmitError, classes.errorLink, editorErr]);

  const [createCancelButtonCooldown, setCreateCancelButtonCooldown] =
    useState(false);

  const breakpoint = useBreakpoint();
  const isMobile = ['xs', 'sm'].indexOf(`${breakpoint}`) > -1;
  const placeholder = 'Type or paste your script here...';
  const handlePendingReplacementsElseCreateOrCancel = () => {
    const pendingReplacements = determinePendingReplacements(
      editorState.script.text
    );
    if (canAccessCuesMode && pendingReplacements.length !== 0) {
      dispatch({
        type: 'OpenReplacementsMigrationPrompt',
        payload: {
          pendingReplacements,
          onStart: () => {
            eventTracking.track({
              type: EventName.PhoneticReplacementMigrationPrompt,
              data: {
                projectId,
                action: 'start',
                totalReplacements: pendingReplacements.length,
              },
            });
          },
          onSuccess: (originalWord: string, replacementWord: string) => {
            coreEditor?.helpers.findAndReplace(originalWord, replacementWord);
          },
          onAllComplete: () => {
            eventTracking.track({
              type: EventName.PhoneticReplacementMigrationPrompt,
              data: {
                projectId,
                action: 'complete',
                totalReplacements: pendingReplacements.length,
              },
            });
            handleCreateCancelButtonOnClick();
          },
        },
      });
    } else {
      handleCreateCancelButtonOnClick();
    }
  };

  useEffect(() => {
    if (createCancelButtonCooldown) {
      const timeoutId = setTimeout(() => {
        setCreateCancelButtonCooldown(false);
      }, 250);
      return () => {
        clearTimeout(timeoutId);
      };
    }
  }, [createCancelButtonCooldown]);

  const handlePreviewCancelButtonOnClick = useCallback(() => {
    setCreateCancelButtonCooldown(true);

    if (isTTSRequestLoading) {
      cancelPreviewFn();
      return;
    }

    previewFn();
  }, [isTTSRequestLoading, previewFn, cancelPreviewFn]);

  const handleCreateCancelButtonOnClick = useCallback(() => {
    if (loading) {
      setCreateCancelButtonCooldown(true);
      cancel();
      return;
    }

    if (editorState.error) return;

    setCreateCancelButtonCooldown(true);
    setActiveSubmitError(null);
    setUpgradePlanError(false);
    create({ canAccessCuesMode });
    if (upgradePlanError) {
      setActiveSubmitError(errorToUse);
    }
  }, [
    loading,
    editorState.error,
    create,
    canAccessCuesMode,
    upgradePlanError,
    cancel,
    errorToUse,
  ]);

  const isFirstRender = useIsFirstRender();

  /**
   * Triggered on first render of editor so we can reset states
   */
  const handleFirstRender = useCallback(
    (initialEditorState: EditorState) => {
      const editorHasCues = hasCuesUtil(initialEditorState);
      appDispatch(setHasCues(editorHasCues));
      appDispatch(setShouldBeSingleTakeOnly(editorHasCues));
    },
    [appDispatch]
  );

  const handleChange = (text: string) => {
    /**
     * Due to PhoneticReplacement.tsx setting the initial replacements on first load,
     * an extra initial call to handleChange is triggered that we want to skip
     * because it is called before we reset the editor state.
     */
    if (isFirstRender) {
      return;
    }
    const json = coreEditor?.helpers.getJSON();
    appDispatch(setScript({ text, textFormat: '', json }));

    const editorHasCues = coreEditor?.helpers.hasCues() || false;
    if (hasCues !== editorHasCues) {
      appDispatch(setHasCues(editorHasCues));
    }

    if (editorState.shouldBeSingleTakeOnly !== editorHasCues) {
      appDispatch(setShouldBeSingleTakeOnly(editorHasCues));
    }
  };

  const { counter, a11yCounterLiveLabel, a11yCounterStaticLabel, tooltip } =
    getFormattedEditorLimits({
      charactersUsed: editorState.finalScript.charactersUsed,
      characterLimit: editorState.characterLimit,
      paragraphsUsed: editorState.finalScript.paragraphsUsed,
      sentencesUsed: editorState.finalScript.sentencesUsed,
      renderMode: editorState.renderMode,
    });

  // note this will also count empty spaces as 0 until a valid input is provided
  const emptyEditor = editorState.finalScript.charactersUsed === 0;

  const scriptModeButtonProps = {
    onButtonClick: isCuesMode
      ? handlePreviewCancelButtonOnClick
      : handlePendingReplacementsElseCreateOrCancel,
    onDropdownSelect: handleOnRenderModeChanged,
    loading: loading || isTTSRequestLoading,
    editorState,
    createCancelButtonCooldown,
    selectDropdownDisabled:
      hasCues ||
      projectVersionFeatures.renderMultipleClipsDisabled ||
      emptyEditor ||
      Boolean(isLocked) ||
      !isEditable,
    isCuesMode,
    // check emptyEditor and !loading to ensure cancel action is available
    // make sure to check we are in cues mode to avoid edgecase where we attempt
    // a preview cue render and switch back to script afterwards and have a partially
    // disabled button
    disabled:
      (!hasCueChanged && editorState.editMode === EditMode.CUES) ||
      (emptyEditor && !loading) ||
      Boolean(isLocked) ||
      !isEditable,
  };

  // Determine text direction based on project type
  const textDirection = useMemo(() => {
    if (
      project.studioVersion === StudioProjectVersion.AzurePersian ||
      project.studioVersion === StudioProjectVersion.AzureArabic
    ) {
      return 'rtl';
    }
    return 'ltr';
  }, [project.studioVersion]);

  useEffect(() => {
    // listen to changes in the editor state
    const handler = (e: Event) => {
      const customEvent = e as CustomEvent<{ text: string }>;
      const { text } = customEvent.detail;
      appDispatch(repopulate({ textFormat: text?.trim() }));
    };
    document.addEventListener('import_script_text_extracted', handler);
    return () =>
      document.removeEventListener('import_script_text_extracted', handler);
  }, [appDispatch]);

  return (
    <>
      <EditorV2
        data-e2e="project-editor"
        projectId={projectId}
        isMobile={isMobile}
        placeholder={placeholder}
        onChange={handleChange}
        onFirstRender={handleFirstRender}
        isEditable={isEditable}
        extensionOptionsOverrides={extensionOptionsOverrides}
        direction={textDirection}
      />
      <>
        <Box paddingTop={5}>
          {isLocked && (
            <Box paddingBottom={1}>
              <Typography variant="pMd" color="gray_0">
                This voice is not available, select a different one to generate
                a take.
              </Typography>
            </Box>
          )}
          <Box
            alignItems="center"
            display="flex"
            flexWrap="wrap"
            gap={2}
            justifyContent="flex-start"
          >
            <Box>
              {projectVersionFeatures.renderMultipleClipsDisabled ? (
                <CJKEditorPrimaryActionButton {...scriptModeButtonProps} />
              ) : (
                <EditorPrimaryActionButton {...scriptModeButtonProps} />
              )}
            </Box>
            {!isCuesMode && Boolean(editorState.finalScript.charactersUsed) && (
              <Box alignItems="center" display="flex" flexShrink={0}>
                <VisuallyHidden aria-live="polite" id="editor-input-limit">
                  {a11yCounterLiveLabel}
                </VisuallyHidden>
                <Box tabIndex={0}>
                  <VisuallyHidden>{a11yCounterStaticLabel}</VisuallyHidden>
                  <Typography color="tertiary" variant="pMd" aria-hidden>
                    {counter}
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>
        </Box>
        <EditorError
          inlineError={inlineError}
          tooltip={tooltip}
          upgradePlanError={upgradePlanError}
        />
      </>
    </>
  );
};

export default Editor;
